import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight, CalendarCheck, Gift } from "lucide-react";
import { RevealText, Reveal } from "@/components/Reveal";
import Img from "@/components/Img";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SESSIONS, BUNDLES, BOOKING_URL } from "@/data/content";

// Screenshot-style pricing card used inside the package pop-up
const PackageCard = ({ t, s, idx }) => {
  const [subOpen, setSubOpen] = React.useState(false);
  const hasSub = Array.isArray(t.subPackages) && t.subPackages.length > 0;

  return (
    <div
      data-testid={`package-card-${s.key}-${idx}`}
      className={`relative flex flex-col rounded-md p-5 md:p-6 transition-colors ${
        t.popular ? "border-2 border-clay bg-white/60" : "border border-ink/15 bg-cream"
      }`}
    >
      {t.popular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-clay px-4 py-1 text-[10px] font-medium uppercase tracking-[0.22em] text-ink">
          Most Popular
        </span>
      )}
      <h4 className="font-serif text-xl md:text-2xl text-ink leading-tight">{t.name}</h4>
      {t.price && <p className="mt-2 font-serif text-3xl md:text-4xl text-sage leading-none">{t.price}</p>}
      {!t.price && t.priceFrom && (
        <p className="mt-2 font-serif text-3xl md:text-4xl text-sage leading-none">
          from {t.priceFrom}
        </p>
      )}
      {t.retainer && (
        <p className="mt-2 text-[10px] uppercase tracking-[0.16em] text-inkmuted leading-snug">{t.retainer}</p>
      )}
      {t.desc && <p className="mt-3 text-[13px] text-inkmuted leading-relaxed">{t.desc}</p>}
      <ul className="mt-5 space-y-2 flex-1">
        {(t.features || []).map((f) => (
          <li key={f} className="flex items-start gap-2 text-[13px] text-inksoft leading-snug">
            <Check size={13} className="mt-0.5 shrink-0 text-sage" />
            {f}
          </li>
        ))}
      </ul>

      {hasSub ? (
        <>
          <button
            type="button"
            onClick={() => setSubOpen(true)}
            data-testid={`view-sub-${s.key}-${idx}`}
            className="mt-5 inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-medium border border-ink text-ink hover:bg-ink hover:text-cream transition-colors"
          >
            View Packages
          </button>
          <Dialog open={subOpen} onOpenChange={setSubOpen}>
            <DialogContent
              data-testid={`sub-dialog-${s.key}-${idx}`}
              className="max-w-5xl max-h-[88vh] overflow-y-auto bg-cream border-ink/10"
            >
              <DialogHeader>
                <DialogTitle className="font-serif font-light text-3xl md:text-4xl text-ink text-left">
                  {t.name}
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {t.subPackages.map((sp, sidx) => (
                  <div
                    key={sp.name}
                    data-testid={`sub-card-${s.key}-${sidx}`}
                    className="relative flex flex-col rounded-md p-7 border border-ink/15 bg-white/60"
                  >
                    <h4 className="font-serif text-xl text-ink">{sp.name}</h4>
                    <p className="mt-2 font-serif text-4xl text-sage">{sp.price}</p>
                    {sp.retainer && (
                      <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-inkmuted">
                        {sp.retainer}
                      </p>
                    )}
                    {sp.desc && (
                      <p className="mt-3 text-sm text-inkmuted leading-relaxed">{sp.desc}</p>
                    )}
                    <ul className="mt-6 space-y-2.5 flex-1">
                      {(sp.features || []).map((f) => (
                        <li key={f} className="flex items-start gap-2.5 text-sm text-inksoft">
                          <Check size={15} className="mt-0.5 shrink-0 text-sage" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <a
                      href={sp.bookUrl || t.bookUrl || s.bookUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`book-sub-${s.key}-${sidx}`}
                      className="mt-5 inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-medium border border-ink text-ink hover:bg-ink hover:text-cream transition-colors"
                    >
                      Book This Package
                    </a>
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <a
          href={t.bookUrl || s.bookUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`book-${s.key}-${idx}`}
          className={`mt-5 inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
            t.popular
              ? "bg-ink text-cream hover:bg-sage"
              : "border border-ink text-ink hover:bg-ink hover:text-cream"
          }`}
        >
          Book This Package
        </a>
      )}
    </div>
  );
};

const SessionRow = ({ s, i }) => {
  const single = s.tiers.length === 1;
  const flip = i % 2 === 1;
  return (
    <div
      data-testid={`session-${s.key}`}
      className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 items-center ${
        flip ? "md:[&>*:first-child]:order-2" : ""
      }`}
    >
      <div className="md:col-span-6">
        <Img
          src={s.img}
          alt={s.name}
          className={`aspect-[4/3] group ${flip ? "rounded-tr-[3.5rem]" : "rounded-tl-[3.5rem]"}`}
          imgClassName="group-hover:scale-105"
        />
      </div>

      <div className="md:col-span-6">
        <span className="font-serif text-sage/50 text-5xl leading-none">
          {String(i + 1).padStart(2, "0")}
        </span>
        <h2 className="mt-2 font-serif font-light text-ink text-4xl md:text-5xl leading-tight">
          {s.name}
        </h2>
        <p className="mt-3 max-w-lg font-serif italic text-sage text-lg md:text-xl leading-snug">
          {s.blurb}
        </p>
        {s.description && (
          <p className="mt-5 max-w-lg text-inksoft leading-relaxed">{s.description}</p>
        )}

        {single ? (
          <>
            <p className="mt-6 font-serif text-5xl text-sage">
              {s.tiers[0].price}
              <span className="ml-2 font-sans text-base text-inkmuted">/ session</span>
            </p>
            <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 max-w-lg">
              {s.includes.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-inksoft">
                  <Check size={15} className="text-sage shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            {s.finePrint && (
              <p
                data-testid={`session-fineprint-${s.key}`}
                className="mt-6 max-w-lg text-xs md:text-[13px] leading-relaxed text-inkmuted italic border-l-2 border-sage/40 pl-4"
              >
                {s.finePrint}
              </p>
            )}
            <a
              href={s.bookUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`book-${s.key}`}
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-8 py-3.5 text-sm font-medium text-cream transition-colors hover:bg-sage"
            >
              Book {s.name}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </>
        ) : (
          <>
            <p className="mt-6 text-inksoft">
              <span className="font-serif text-3xl text-sage">{s.tiers.length} packages</span>
              <span className="ml-3 text-sm text-inkmuted">starting at {s.tiers[0].price}</span>
            </p>
            {s.finePrint && (
              <p
                data-testid={`session-fineprint-${s.key}`}
                className="mt-4 max-w-lg text-xs md:text-[13px] leading-relaxed text-inkmuted italic border-l-2 border-sage/40 pl-4"
              >
                {s.finePrint}
              </p>
            )}
            <Dialog>
              <DialogTrigger asChild>
                <button
                  data-testid={`view-packages-${s.key}`}
                  className="group mt-7 inline-flex items-center gap-2 rounded-full bg-ink px-8 py-3.5 text-sm font-medium text-cream transition-colors hover:bg-sage"
                >
                  View packages
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </DialogTrigger>
              <DialogContent
                data-testid={`packages-dialog-${s.key}`}
                className="max-w-3xl max-h-[92vh] p-0 gap-0 bg-cream border-ink/10 flex flex-col"
              >
                <DialogHeader className="px-6 md:px-8 pt-6 md:pt-8 pb-4 border-b border-ink/10 shrink-0">
                  <DialogTitle className="font-serif font-light text-3xl md:text-4xl text-ink text-left">
                    {s.name} — packages
                  </DialogTitle>
                  <p className="text-sm text-inkmuted mt-1">
                    Pick a package below to see details.
                  </p>
                </DialogHeader>
                <Tabs
                  defaultValue={`${s.key}-0`}
                  className="flex-1 flex flex-col overflow-hidden"
                >
                  {/* Package chooser — pill row of package names + prices */}
                  <div className="border-b border-ink/10 px-4 md:px-6 py-4 shrink-0 overflow-x-auto">
                    <TabsList className="inline-flex h-auto bg-transparent p-0 gap-2 flex-wrap">
                      {s.tiers.map((t, idx) => (
                        <TabsTrigger
                          key={t.name}
                          value={`${s.key}-${idx}`}
                          data-testid={`tab-${s.key}-${idx}`}
                          className="rounded-full border border-ink/15 bg-cream px-4 py-2 text-[13px] text-inksoft data-[state=active]:bg-ink data-[state=active]:text-cream data-[state=active]:border-ink transition-colors whitespace-nowrap"
                        >
                          <span className="font-medium">{t.name}</span>
                          <span className="ml-2 opacity-70">
                            {t.price || (t.priceFrom ? `from ${t.priceFrom}` : "")}
                          </span>
                          {t.popular && (
                            <span className="ml-2 text-[9px] uppercase tracking-wider text-clay data-[state=active]:text-clay">
                              ★
                            </span>
                          )}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </div>
                  {/* Selected package details */}
                  <div className="flex-1 overflow-y-auto px-6 md:px-10 py-8">
                    {s.tiers.map((t, idx) => (
                      <TabsContent
                        key={t.name}
                        value={`${s.key}-${idx}`}
                        data-testid={`tab-content-${s.key}-${idx}`}
                        className="mt-0"
                      >
                        <PackageCard t={t} s={s} idx={idx} />
                      </TabsContent>
                    ))}
                  </div>
                </Tabs>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
};

export default function Investment() {
  return (
    <div className="pt-8 md:pt-12">
      <section className="mx-auto max-w-[1400px] px-6 md:px-10">
        <p className="overline text-sage mb-6">Investment</p>
        <RevealText
          as="h1"
          className="font-serif font-light text-ink leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl"
          lines={["Collections", "& pricing"]}
        />
        <Reveal delay={0.3}>
          <p className="mt-8 max-w-xl text-inksoft leading-relaxed">
            Every collection includes pre-session styling guidance, a relaxed shoot, and a
            beautifully edited private gallery. Explore each session below and tap Book to reserve.
          </p>
        </Reveal>
      </section>

      {/* Session types — full-width rows; multi-package sessions open a pricing pop-up */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 mt-20 space-y-20 md:space-y-28">
        {SESSIONS.map((s, i) => (
          <Reveal key={s.key} y={60}>
            <SessionRow s={s} i={i} />
          </Reveal>
        ))}
      </section>

      {/* Every Session Includes — universal fine print */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 mt-28 md:mt-36">
        <Reveal>
          <div
            data-testid="every-session-includes"
            className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start bg-sagelight/50 rounded-tl-[4rem] rounded-br-[4rem] p-10 md:p-16"
          >
            <div className="md:col-span-4">
              <p className="overline text-sagedark mb-4">The fine print</p>
              <h2 className="font-serif font-light text-ink text-4xl md:text-5xl leading-[1.02] tracking-tight">
                Every session includes
              </h2>
            </div>
            <div className="md:col-span-8 space-y-5 text-inksoft leading-relaxed">
              <p>
                Your session includes my time and expertise, a carefully curated collection
                of professionally edited, high-resolution digital images, and a private
                online gallery where you can download your selected images, share them with
                family and friends, purchase your full gallery, and order heirloom-quality
                prints and products through a professional print lab.
              </p>
              <p>
                A non-refundable retainer and signed contract are required to reserve your
                session date. The remaining balance is due on or before your scheduled
                session.
              </p>
              <p className="italic font-serif text-inkmuted">
                Please note: Louisiana sales tax will be added to all session prices.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Bundles pop-up */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
        <Reveal>
          <div className="flex flex-col items-start gap-8 rounded-tl-[4rem] rounded-br-[4rem] bg-sage p-10 md:flex-row md:items-center md:justify-between md:p-16">
            <div className="max-w-xl">
              <p className="overline text-cream/70 mb-4 flex items-center gap-2">
                <Gift size={15} /> Bundle & save
              </p>
              <h2 className="font-serif font-light text-cream text-4xl md:text-6xl leading-tight">
                All-inclusive baby packages
              </h2>
              <p className="mt-4 text-cream/80 leading-relaxed">
                Follow your little one&rsquo;s first year with a curated bundle — from maternity
                all the way to the cake smash.
              </p>
            </div>

            <Dialog>
              <DialogTrigger asChild>
                <button
                  data-testid="view-bundles-btn"
                  className="group inline-flex items-center gap-3 rounded-full bg-cream px-9 py-4 text-sm font-medium text-ink transition-colors hover:bg-ink hover:text-cream"
                >
                  View baby bundles
                  <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </DialogTrigger>
              <DialogContent
                data-testid="bundles-dialog"
                className="max-w-3xl max-h-[85vh] overflow-y-auto bg-cream border-ink/10"
              >
                <DialogHeader>
                  <DialogTitle className="font-serif font-light text-3xl md:text-4xl text-ink text-left">
                    All-inclusive baby packages
                  </DialogTitle>
                </DialogHeader>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {BUNDLES.map((b, i) => (
                    <div
                      key={b.name}
                      data-testid={`bundle-${i}`}
                      className="flex flex-col rounded-tr-[2rem] border border-ink/10 bg-white/60 p-6"
                    >
                      <h3 className="font-serif text-2xl text-ink leading-tight">{b.name}</h3>
                      <p className="mt-1 text-sm text-inkmuted">{b.blurb}</p>
                      {b.description && (
                        <p className="mt-3 text-sm text-inksoft leading-relaxed">{b.description}</p>
                      )}
                      <p className="mt-3 font-serif text-3xl text-sage">
                        {b.originalPrice && (
                          <span className="mr-3 text-xl text-inkmuted line-through">{b.originalPrice}</span>
                        )}
                        {b.price}
                        {b.priceNote && (
                          <span className="ml-2 text-sm tracking-[0.14em] uppercase text-inksoft align-middle">{b.priceNote}</span>
                        )}
                      </p>
                      {b.savingsLabel && (
                        <p className="mt-1 text-xs tracking-[0.16em] uppercase text-sage">{b.savingsLabel}</p>
                      )}
                      <ul className="mt-4 space-y-2">
                        {b.includes.map((f) => (
                          <li key={f} className="flex items-start gap-2.5 text-sm text-inksoft">
                            <Check size={15} className="mt-0.5 shrink-0 text-sage" />
                            {f}
                          </li>
                        ))}
                      </ul>
                      {b.bookUrl && (
                        <a
                          href={b.bookUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`bundle-book-${i}`}
                          className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-ink px-6 py-2.5 text-sm font-medium text-cream transition-colors hover:bg-sage"
                        >
                          Book This Bundle
                          <ArrowUpRight size={14} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </Reveal>
      </section>

      {/* Custom sessions note */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 mt-8 md:mt-14 mb-4">
        <Reveal>
          <div
            data-testid="investment-custom-note"
            className="mx-auto max-w-3xl text-center border-t border-b border-ink/10 py-10 md:py-14"
          >
            <p className="overline text-sage mb-4">A note from Jacqueline</p>
            <p className="font-serif italic text-2xl md:text-3xl text-ink leading-snug">
              Don't see exactly what you're looking for?
            </p>
            <p className="mt-4 text-inksoft leading-relaxed max-w-xl mx-auto">
              Reach out. I'm always happy to create a session that fits your vision —
              whether it's something custom, seasonal, or a little outside the box.
            </p>
          </div>
        </Reveal>
      </section>

      {/* Booking CTA */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-28 md:pb-36">
        <Reveal>
          <div className="rounded-tl-[4rem] rounded-br-[4rem] bg-sagelight p-10 md:p-20 text-center">
            <CalendarCheck size={34} className="text-sagedark mx-auto" strokeWidth={1.5} />
            <h2 className="mt-6 font-serif font-light text-ink text-4xl md:text-6xl leading-tight">
              Ready to reserve your date?
            </h2>
            <p className="mt-5 max-w-lg mx-auto text-inksoft leading-relaxed">
              A few of my sessions are open for instant booking — the rest, I love to chat about first so we can build something that fits.
              Whichever feels right, I'll be on the other end.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="investment-booking-btn"
                className="group inline-flex items-center gap-3 rounded-full bg-sage text-cream px-9 py-4 text-sm font-medium hover:bg-sagedark transition-colors"
              >
                I'm ready to book
                <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <Link
                to="/contact"
                data-testid="investment-contact-link"
                className="inline-flex items-center gap-2 rounded-full border border-ink px-9 py-4 text-sm font-medium text-ink hover:bg-ink hover:text-cream transition-colors"
              >
                Let's chat first
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
